import { Autocomplete, TextField } from '@mui/material';
import { AxiosResponse } from 'axios';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useLayoutContext } from '../../../../Context/Layout.context';
import { AssessmentSessionProcessTypeId } from '../../../../services/exams/assessmentSession/assessments.types';
import { getAvailableAssessmentSessionProcesses } from '../../../../services/exams/assessmentSessionProcesses/assessmentSessionProcesses.service';
import {
  deleteAssessmentSessionProcessAssignment,
  assignAssessmentSessionProcess,
  getAssignedAssessmentSessionProcess,
} from '../../../../services/scoring/scoring.service';
import { getProcessLabel } from '../../../../utils/assessmentSessionProcess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Prompt, promptType } from '../../../Prompt/Prompt.component';
import { ExaminerFunction } from '../../../../pages/Assessment/Assessment.types';

interface ProcessQualificationProps {
  id: string;
  isVisible: boolean;
  isReadonly: boolean;
  onProcessSelect: (selectedProcess: AssessmentSessionProcessTypeId | null) => void;
  examinerFunction: ExaminerFunction;
}

export const ProcessQualification: React.FC<ProcessQualificationProps> = (props) => {
  const [availableProcessess, setAvailableProcessesses] = useState<AssessmentSessionProcessTypeId[]>([]);
  const [selectedProcess, setSelectedProcess] = useState<AssessmentSessionProcessTypeId | null>(null);
  const { openAlert } = useLayoutContext();
  const [prompt, setPrompt] = useState<{
    isOpen: boolean;
    type: promptType;
    mainText: string;
    onConfirm?: () => void;
    mainActionButtonText?: string;
    onCancel?: () => void;
    cancelButtonText?: string;
  }>({
    isOpen: false,
    type: 'confirmation',
    mainText: '',
  });

  const isPZWO = props.examinerFunction === ExaminerFunction.PZWO;
  const isKK = props.examinerFunction === ExaminerFunction.KK;

  useEffect(() => {
    let cancel = false;
    if (props.isVisible) {
      getAssignedAssessmentSessionProcess(props.id).then(
        (res: AxiosResponse<AssessmentSessionProcessTypeId | null>) => {
          if (cancel) return;
          if (res.status === 204)
            //no content
            setSelectedProcess(null);
          else setSelectedProcess(res.data);
        },
        () => setSelectedProcess(null),
      );
      if (!props.isReadonly) {
        getAvailableAssessmentSessionProcesses().then(
          (res: AxiosResponse<AssessmentSessionProcessTypeId[]>) => {
            if (cancel) return;
            setAvailableProcessesses(res.data ? res.data : []);
          },
          () => setAvailableProcessesses([]),
        );
      }
    }
    return () => {
      cancel = true;
    };
  }, [props.id, props.isVisible, props.isReadonly]);

  useEffect(() => {
    props.onProcessSelect(selectedProcess);
  }, [selectedProcess]);

  const hidePrompt = () => {
    setPrompt((prev) => ({ ...prev, isOpen: false }));
  };

  const displayError = (isTechnicalError: boolean, clearSelectedProcess?: boolean) => {
    isKK
      ? setPrompt({
          isOpen: true,
          type: 'error',
          mainText: 'Zmiana procesu nie powiodła się. Spróbuj ponownie później.',
          mainActionButtonText: 'POWRÓT',
          onConfirm: () => hidePrompt(),
        })
      : openAlert('error');
    console.error(
      `${isTechnicalError ? 'Technical' : 'Business'} error occured when saving selected AssessmentSessionProcess`,
    );
    if (clearSelectedProcess) setSelectedProcess(null);
  };

  const handleAutocompleteChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: AssessmentSessionProcessTypeId | null,
  ) => {
    if (newValue !== null && newValue !== selectedProcess) {
      assignAssessmentSessionProcess(props.id, newValue).then(
        (res: boolean) => {
          if (res) {
            setSelectedProcess(newValue);
            if (isKK) {
              setPrompt({
                isOpen: true,
                type: 'success',
                mainText: 'Zmiana procesu zakończona sukcesem.',
                mainActionButtonText: 'POWRÓT',
                onConfirm: () => hidePrompt(),
              });
            }
          } else {
            displayError(false, true);
          }
        },
        () => {
          displayError(true, true);
        },
      );
    } else if (newValue === null) {
      deleteAssessmentSessionProcessAssignment(props.id).then(
        (res: boolean) => {
          if (res) {
            setSelectedProcess(null);
          } else {
            displayError(false, false);
          }
        },
        () => {
          displayError(true, false);
        },
      );
    }
  };

  const displayConfirmationPrompt = (
    event: SyntheticEvent<Element, Event>,
    newValue: AssessmentSessionProcessTypeId | null,
  ) => {
    setPrompt({
      isOpen: true,
      type: 'confirmation',
      mainText: 'Czy na pewno chcesz dokonać zmiany procesu?',
      onConfirm: () => {
        hidePrompt();
        handleAutocompleteChange(event, newValue);
      },
      mainActionButtonText: 'TAK',
      onCancel: () => hidePrompt(),
    });
  };

  const borderStyle = isPZWO && selectedProcess === null ? '2px solid #FF9A02' : '1px solid rgba(44, 45, 62, 0.5)';

  if (props.isVisible)
    return (
      <>
        {isPZWO && selectedProcess === null && (
          <InfoOutlinedIcon id={'warningIcon'} htmlColor="#FF9A02" sx={{ marginTop: '2px', marginRight: '8px' }} />
        )}
        <Autocomplete
          value={selectedProcess}
          sx={{
            width: 200,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                border: borderStyle,
              },
            },
          }}
          size={'small'}
          readOnly={props.isReadonly}
          renderInput={(params) => (
            <TextField color={'info'} {...params} disabled={props.isReadonly} label="Wybierz proces" />
          )}
          options={availableProcessess.sort()}
          getOptionLabel={(option: AssessmentSessionProcessTypeId) => getProcessLabel(option).short}
          onChange={isKK ? displayConfirmationPrompt : handleAutocompleteChange}
          noOptionsText={'Brak procesów'}
          openText={'Otwórz'}
          clearText={'Wyczyść'}
        />
        <Prompt
          type={prompt.type}
          open={prompt.isOpen}
          mainText={prompt.mainText}
          onConfirm={prompt.onConfirm}
          mainActionButtonText={prompt.mainActionButtonText}
          onCancel={prompt.onCancel}
          cancelButtonText={prompt.cancelButtonText}
        />
      </>
    );
  else return <></>;
};
